// src/context/AppContext.jsx - Art of Story Global App Context with Firebase Integration
// Manages global app state with automatic Firebase sync for multi-device support

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  saveAssessmentResults,
  saveModuleProgress,
  saveToolData,
  saveToolUsage,
  loadAllUserData,
  migrateOldLocalStorage
} from '../services/storageService';

const AppContext = createContext({});

// eslint-disable-next-line react-refresh/only-export-components -- hook shares the provider's context
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const { user, storagePreference } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assessmentResults, setAssessmentResultsState] = useState(null);
  const [moduleProgress, setModuleProgressState] = useState({});
  const [toolsUsage, setToolsUsageState] = useState({});
  const [toolData, setToolDataState] = useState({});
  const [theme, setTheme] = useState('dark');
  const [notifications, setNotifications] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load user data when user logs in or changes
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) {
        setLoading(false);
        setDataLoaded(false);
        return;
      }

      setLoading(true);

      try {
        // Migrate old localStorage keys first
        migrateOldLocalStorage();

        console.log('📥 Loading user data for:', user.uid);

        // Load all user data from Firebase/localStorage
        const data = await loadAllUserData(user.uid, storagePreference);

        // Update state with loaded data
        if (data.assessmentResults) {
          setAssessmentResultsState(data.assessmentResults);
        }
        if (data.moduleProgress && Object.keys(data.moduleProgress).length > 0) {
          setModuleProgressState(data.moduleProgress);
        }
        if (data.toolData && Object.keys(data.toolData).length > 0) {
          setToolDataState(data.toolData);
        }
        if (data.toolUsage && Object.keys(data.toolUsage).length > 0) {
          setToolsUsageState(data.toolUsage);
        }

        setDataLoaded(true);
        console.log('✅ User data loaded successfully');
      } catch (error) {
        console.error('❌ Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user?.uid, storagePreference]);

  // Wrapper function to save assessment results
  const setAssessmentResults = async (results) => {
    setAssessmentResultsState(results);

    if (user?.uid && results) {
      await saveAssessmentResults(user.uid, results, storagePreference);
    }
  };

  // Module progress helper functions with auto-save
  const getModuleProgress = (moduleId) => {
    return moduleProgress?.[moduleId] || { completed: false, started: false };
  };

  const updateModuleProgress = async (moduleId, progressData) => {
    const updatedProgress = {
      ...moduleProgress,
      [moduleId]: {
        ...moduleProgress?.[moduleId],
        ...progressData,
        updatedAt: new Date().toISOString()
      }
    };

    setModuleProgressState(updatedProgress);

    // Save to Firebase if user is logged in
    if (user?.uid) {
      await saveModuleProgress(user.uid, moduleId, updatedProgress[moduleId], storagePreference);
    }
  };

  const completeModule = async (moduleId) => {
    const completionData = {
      ...moduleProgress?.[moduleId],
      completed: true,
      completedAt: new Date().toISOString(),
      progress: 100
    };

    const updatedProgress = {
      ...moduleProgress,
      [moduleId]: completionData
    };

    setModuleProgressState(updatedProgress);

    // Save to Firebase if user is logged in
    if (user?.uid) {
      await saveModuleProgress(user.uid, moduleId, completionData, storagePreference);
    }
  };

  const isModuleCompleted = (moduleId) => {
    return moduleProgress?.[moduleId]?.completed || false;
  };

  // Tool data helpers with auto-save
  const updateToolData = async (toolId, data) => {
    const updatedData = {
      ...toolData,
      [toolId]: {
        ...toolData?.[toolId],
        ...data,
        updatedAt: new Date().toISOString()
      }
    };

    setToolDataState(updatedData);

    // Save to Firebase if user is logged in
    if (user?.uid) {
      await saveToolData(user.uid, toolId, updatedData[toolId], storagePreference);
    }
  };

  const updateToolUsage = async (toolId, usage) => {
    const updatedUsage = {
      ...toolsUsage,
      [toolId]: {
        ...toolsUsage?.[toolId],
        ...usage,
        updatedAt: new Date().toISOString()
      }
    };

    setToolsUsageState(updatedUsage);

    // Save to Firebase if user is logged in
    if (user?.uid) {
      await saveToolUsage(user.uid, toolId, updatedUsage[toolId], storagePreference);
    }
  };

  const value = {
    user,
    loading,
    dataLoaded,

    // Assessment
    assessmentResults,
    setAssessmentResults,

    // Module Progress
    moduleProgress,
    setModuleProgress: setModuleProgressState, // Direct setter (use updateModuleProgress instead)
    getModuleProgress,
    updateModuleProgress,
    completeModule,
    isModuleCompleted,

    // Tool Data & Usage
    toolData,
    toolsUsage,
    updateToolData,
    updateToolUsage,
    setToolsUsage: setToolsUsageState, // Direct setter (use updateToolUsage instead)

    // UI Settings
    theme,
    setTheme,
    notifications,
    setNotifications
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};